
import Vue, { PropType } from 'vue';
import { DocumentRequestTypes, OnlinePortalApplicationDocumentRequest } from '../../models';
import DocumentUploadHeader from './document-upload-header.vue';
import { ApplicationDocumentRequest, DocumentName, LoanApplication, Customer } from '@sageworks/jpi';
import { fetchCustomerByIds } from '../../logic/Customer.service';
import { SignatureType } from '@sageworks/core-logic/src/enums/signature-type';

const POLLING_INTERVAL = 5000;
const MAX_POLLING_AMOUNT = 450000; // 7.5 minutes

export default Vue.extend({
	name: 'EsignForm',
	components: { DocumentUploadHeader },
	props: {
		documentRequest: Object as PropType<OnlinePortalApplicationDocumentRequest>
	},
	data() {
		return {
			sendingForEsign: false,
			statusPollingIntervalId: null as number | null,
			customerType: null as Customer.TypeEnum | null
		};
	},
	computed: {
		loanApplication(): LoanApplication | null {
			return this.directStore.state.LoanApplicationData.loanApplication;
		},
		isTemplateInProgress() {
			return this.esignTemplateStatus === 'IN_PROGRESS';
		},
		isTemplateSigned() {
			return this.esignTemplateStatus === 'SIGNED';
		},
		isLenderView(): boolean {
			return this.directStore.getters.User.isLender;
		},
		disabledButtonMessage() : string {
			if (this.isLenderView) {
				return 'E-sign templates can only be completed by the assigned applicant.';
			}

			// For non-individual applicants, show initiator email if available
			if (this.customerType !== Customer.TypeEnum.Person && !this.isCurrentUserTemplateInitiator && this.templateInitiatorEmail) {
				return `${this.templateInitiatorEmail} initiated the outstanding e-sign session and must complete it.`;
			}

			return 'Action can only be completed by the assigned applicant.';
		},
		isApplicationDocumentRequest(): boolean {
			return this.documentRequest.documentRequestType === DocumentRequestTypes.LoanApplicationDocumentRequest;
		},
		isOpenApplicationDocumentRequest(): boolean {
			return (
				!this.documentRequest.dateUploaded &&
				!this.documentRequest.dateWaived &&
				!(this.documentRequest as ApplicationDocumentRequest).waivedReason &&
				!(this.documentRequest as ApplicationDocumentRequest).waivedByLoanOfficerId &&
				!(this.documentRequest as ApplicationDocumentRequest).notUploadedReason &&
				this.esignTemplateStatus !== 'SIGNED'
			);
		},
		canInitiateSigning(): boolean {
			// Check S2 flag: showDocusignTemplatesInApplication must be true to show Sign Document button
			// NOTE: S2 flag is Docusign-specific. For Adobe users, we skip this check.
			const signatureType = this.directStore.state.ESignModule.signatureType;
			const s2Flag = this.directStore.state.ESignModule.showDocusignTemplatesInApplication;

			if (signatureType === SignatureType.DOCUSIGN && !s2Flag) {
				return false;
			}

			// For individual applicants (Person): Only signers can initiate signing
			// For non-individual applicants (Business/Farm/NonProfit): Template initiators can also initiate signing
			if (this.customerType === Customer.TypeEnum.Person) {
				// Individual applicant: only signers can initiate
				return this.isCurrentUserSigner;
			} else {
				// Non-individual applicant: both template initiators and signers can initiate
				return this.isCurrentUserTemplateInitiator || this.isCurrentUserSigner;
			}
		},
		documentName(): DocumentName | null {
			const { documentNamesId } = this.documentRequest;
			if (documentNamesId == null || documentNamesId <= 0) {
				return null;
			}

			return this.directStore.getters.DocumentName.documentNameById(documentNamesId);
		},
		adobeTemplateMappingId(): number | null | undefined {
			return this.documentName?.adobeTemplateDocumentNameMappingsId;
		},
		docusignTemplateMappingId(): number | null | undefined {
			return this.documentName?.docusignTemplateDocumentNameMappingId;
		},
		esignTemplateStatus(): string | undefined {
			const statusMap = this.directStore.state.ESignModule.esignTemplateStatuses;
			if (this.documentRequest.id == null) {
				return undefined;
			}

			return statusMap.get(this.documentRequest.id);
		},
		isCurrentUserSigner(): boolean {
			const signerMap = this.directStore.state.ESignModule.signerMap;
			if (this.documentRequest.id == null) {
				return false;
			}

			return signerMap.get(this.documentRequest.id) || false;
		},
		isCurrentUserTemplateInitiator(): boolean {
			const templateInitiatorMap = this.directStore.state.ESignModule.templateInitiatorMap;
			if (this.documentRequest.id == null) {
				return false;
			}

			return templateInitiatorMap.get(this.documentRequest.id) || false;
		},
		templateInitiatorEmail(): string {
			const templateInitiatorEmailMap = this.directStore.state.ESignModule.templateInitiatorEmailMap;
			if (this.documentRequest.id == null) {
				return '';
			}

			return templateInitiatorEmailMap.get(this.documentRequest.id) || '';
		},
		canContinueSigning(): boolean {
			// For individual applicants (Person): Allow both template initiators AND signers to continue signing
			// For non-individual applicants (Business/Farm/NonProfit): Only allow template initiators to continue signing
			if (this.customerType === Customer.TypeEnum.Person) {
				// Individual applicant: both initiators and signers can continue
				return this.isCurrentUserTemplateInitiator || this.isCurrentUserSigner;
			} else {
				return this.isCurrentUserTemplateInitiator;
			}
		},
		continueSigningTooltipMessage(): string {
			if (this.isLenderView) {
				return 'E-sign templates can only be completed by the assigned applicant.';
			}

			// Show initiator email tooltip when current user is neither the template initiator nor a signer
			if (!this.isCurrentUserTemplateInitiator && !this.isCurrentUserSigner && this.templateInitiatorEmail) {
				return `${this.templateInitiatorEmail} initiated the outstanding e-sign session and must complete it.`;
			}

			return 'Action can only be completed by the assigned applicant.';
		}
	},
	async mounted(): Promise<void> {
		// Run independent API calls in parallel (first batch)
		const independentCalls = [
			this.directStore.dispatch.ESignModule.fetchSignatureType({
				applicationType: this.loanApplication?.type
			}),
			this.directStore.dispatch.ESignModule.getSignerMap({
				documentRequestId: this.documentRequest.id,
				applicationType: this.loanApplication?.type
			}),
			this.directStore.dispatch.ESignModule.getTemplateInitiatorMap({
				documentRequestId: this.documentRequest.id,
				applicationType: this.loanApplication?.type
			}),
			this.fetchCustomerType()
		];

		// Get document status in parallel with other calls
		const [, , , , ] = await Promise.all([
			...independentCalls,
			this.getAdobeDocumentStatus()
		]);
	},
	destroyed() {
		this.stopStatusPolling();
	},
	methods: {
		async fetchCustomerType() {
			const { customerId } = this.documentRequest;
			if (customerId == null) {
				return;
			}

			try {
				const customers = await fetchCustomerByIds([customerId]);
				if (customers.length > 0) {
					this.customerType = customers[0].type;
				}
			} catch (error) {
				// Default to null if we can't fetch customer type
				this.customerType = null;
			}
		},
		async resetTemplateSignature() {
			const { documentNamesId, customerId, id } = this.documentRequest;

			if (documentNamesId == null || customerId == null || id == null) {
				this.showToast('E-signature could not be reset.', 'Please try again');
				return;
			}

			try {
				await this.directStore.dispatch.ESignModule.resetTemplateEsignature({
					customerId: customerId,
					documentRequestId: id
				});

				this.getAdobeDocumentStatus();
			} catch {
				this.showToast('E-signature could not be reset.', 'Please try again');
			}
		},
		async getTemplateUrl() {
			this.sendingForEsign = true;
			const url = await this.directStore.dispatch.ESignModule.getTemplateUrl({
				documentRequestId: this.documentRequest.id
			});
			if (url != null) {
				window.open(url);
			} else {
				this.showToast('Esignature Error', 'Url could not be retrieved.');
			}
			this.sendingForEsign = false;
		},
		stopStatusPolling() {
			if (this.statusPollingIntervalId) {
				clearInterval(this.statusPollingIntervalId!);
				this.statusPollingIntervalId = null;
			}
		},
		async startStatusPolling() {
			await this.getAdobeDocumentStatus();

			// If a previous polling interval is still running, clear it
			this.stopStatusPolling();

			if (this.esignTemplateStatus === 'SIGNED') {
				return;
			}

			this.statusPollingIntervalId = setInterval(() => {
				if (this.esignTemplateStatus === 'SIGNED') {
					this.stopStatusPolling();
				}

				this.getAdobeDocumentStatus();
			}, POLLING_INTERVAL);

			setTimeout(() => {
				this.stopStatusPolling();
			}, MAX_POLLING_AMOUNT);
		},
		async sendForEsignature() {
			const { documentNamesId, customerId, id } = this.documentRequest;

			if (documentNamesId == null || customerId == null || id == null) {
				this.showToast('Document could not be sent for E-Signature', 'Please try again.');
				return;
			}

			this.sendingForEsign = true;
			try {
				const response = await this.directStore.dispatch.ESignModule.sendTemplatesForSignature({
					adobeTemplateDocumentNameMappingId: this.adobeTemplateMappingId,
					docusignTemplateDocumentNameMappingId: this.docusignTemplateMappingId,
					customerId: customerId,
					documentRequestId: id
				});

				// Check for specific phone number error
				if (response.errorCode === 'PHONE_NUMBER_INVALID') {
					this.showToast('Invalid Phone Number', response.userMessage, 'danger');
					return; // Exit early, don't start polling
				}

				// Check for general error response from backend
				if (response.error && response.userMessage) {
					this.showToast('E-Signature Error', response.userMessage, 'danger');
					return; // Exit early, don't start polling
				}

				this.handleSignatureResponse(response);

				// Invalidate the cached template initiator value since the user just became the initiator
				await this.directStore.dispatch.ESignModule.invalidateTemplateInitiatorCache({
					documentRequestId: id
				});

				// Refetch the template initiator value to get the updated state
				await this.directStore.dispatch.ESignModule.getTemplateInitiatorMap({
					documentRequestId: id,
					applicationType: this.loanApplication?.type
				});

				await this.startStatusPolling();
			} catch (e) {
				this.showToast('Document could not be sent for E-Signature', 'Please try again.');
			} finally {
				this.sendingForEsign = false;
			}
		},
		handleSignatureResponse(response: any) {
			if (response.url != null) {
				window.open(response.url);
				if (response.webhookError) {
					this.showToast(
						'E-Signature Webhook Error',
						'An issue has occurred and the application can only be submitted once the scheduled job has been completed.'
					);
				}
			} else if (response.phoneNumberError) {
				this.showToast(
					'Document could not be sent for E-Signature',
					'You cannot sign this document as a phone number was not provided for authentication.'
				);
			} else {
				this.showToast('Document could not be sent for E-Signature', 'Url could not be retrieved.');
			}
		},
		async getAdobeDocumentStatus() {
			const { documentNamesId, customerId, id } = this.documentRequest;

			if (documentNamesId == null || customerId == null || id == null) {
				return;
			}

			await this.directStore.dispatch.ESignModule.fetchTemplateStatus({
				documentRequestId: id,
				documentNamesId: documentNamesId
			});
		},
		showToast(title: string, message: string, variant?: string) {
			this.$root.$bvToast.toast(message, {
				title: title,
				solid: true,
				autoHideDelay: 3000,
				noCloseButton: true,
				toaster: 'b-toaster-bottom-right',
				variant: variant
			});
		}
	}
});
